import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import PropTypes from 'prop-types';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '@/context/ThemeContext';
import { useModalStore, useUIStore } from '@/store';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { PLUS_COLORS } from '@/constants/Colors';
import { MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS } from '@/constants/config';
import useResponsiveness from '@/hooks/useResponsiveness';

// "N earlier messages locked" banner shown above the visible tail of a
// conversation for churned-Plus users (PLUS_WINBACK_PRESSURE.md §2).
//
// Two placements via `variant`:
//   `boundary` — split-horizon gradient gate between blurred preview and
//                readable messages (primary CTA).
//   `top`      — slim bar at the scroll ceiling above the blurred preview.
//
// Canonical Plus brand gradient (derived from PLUS_COLORS — NEVER the theme
// accent).
const PLUS_GRADIENT = ['#0D5C47', '#0D5C47'];

const createStyles = (colors, messageWidth, isXS) => StyleSheet.create({
    // Width follows the user's message-width preference (narrow / medium / full),
    // same as MessageGhostUpsell and the message bubbles themselves.
    outerContainer: {
        width: '100%',
        maxWidth:
            messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW :
            messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM : '100%',
        alignSelf: 'center',
        paddingHorizontal: isXS ? 15 : 0,
    },

    pressed: {
        opacity: 0.92,
    },

    // ── Boundary: split-horizon gradient gate ─────────────────────────────
    boundaryContainer: {
        marginTop: 4,
        marginBottom: 16,
        borderRadius: 16,
        overflow: 'hidden',
        ...(Platform.OS === 'web' && {
            boxShadow: '0 12px 36px rgba(73, 90, 255, 0.34)',
        }),
    },
    boundaryGradient: {
        flexDirection: 'row',
        minHeight: 104,
    },
    boundaryLeftPane: {
        width: 96,
        paddingVertical: 14,
        paddingHorizontal: 10,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.22)',
        borderRightWidth: 1,
        borderRightColor: 'rgba(255, 255, 255, 0.14)',
        overflow: 'hidden',
    },
    boundaryStack: {
        position: 'absolute',
        top: 14,
        left: 14,
        right: 8,
        gap: 5,
    },
    boundaryStackBar: {
        height: 7,
        borderRadius: 4,
        backgroundColor: '#fff',
    },
    boundaryLeftContent: {
        alignItems: 'center',
        gap: 2,
        zIndex: 1,
    },
    boundaryLockRing: {
        width: 32,
        height: 32,
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.16)',
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.3)',
        marginBottom: 2,
    },
    boundaryCount: {
        color: '#fff',
        fontVariant: ['tabular-nums'],
        ...(Platform.OS === 'web' && { textShadow: '0 1px 3px rgba(0,0,0,0.25)' }),
    },
    boundaryCountLabel: {
        color: 'rgba(255, 255, 255, 0.72)',
        letterSpacing: 0.8,
        textTransform: 'uppercase',
    },
    boundaryRightPane: {
        flex: 1,
        minWidth: 0,
        paddingVertical: 16,
        paddingHorizontal: 16,
        justifyContent: 'center',
        gap: 5,
    },
    boundaryEyebrow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
    },
    boundaryEyebrowText: {
        color: 'rgba(255, 255, 255, 0.88)',
        letterSpacing: 0.6,
        textTransform: 'uppercase',
    },
    boundaryHeadline: {
        color: '#fff',
        lineHeight: 22,
        ...(Platform.OS === 'web' && { textShadow: '0 1px 2px rgba(0,0,0,0.2)' }),
    },
    boundarySubtext: {
        color: 'rgba(255, 255, 255, 0.82)',
        lineHeight: 17,
    },
    boundaryCta: {
        marginTop: 8,
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 7,
        paddingVertical: 9,
        paddingHorizontal: 14,
        borderRadius: 999,
        backgroundColor: '#fff',
    },
    boundaryCtaText: {
        color: '#5b2bd6',
    },

    // ── Top: slim bar at scroll ceiling ───────────────────────────────────
    slimContainer: {
        marginTop: 0,
        marginBottom: 8,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingVertical: 8,
        paddingHorizontal: 10,
        borderRadius: 11,
        backgroundColor: colors.solid_color_tertiary,
        borderWidth: 1,
        borderColor: colors.border_color_secondary,
    },
    slimDot: {
        width: 26,
        height: 26,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    slimLabel: {
        flex: 1,
        minWidth: 0,
        color: colors.text_color_secondary,
    },
    slimCta: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        flexShrink: 0,
    },
    slimCtaText: {
        color: PLUS_COLORS.textColorPurple,
    },
});

function BoundaryBanner({ hiddenCount, visibleCap, styles, onPress }) {
    return (
        <Pressable
            onPress={onPress}
            accessibilityRole="button"
            accessibilityLabel={`Restore Plus to read ${hiddenCount} earlier messages`}
            style={({ pressed }) => [styles.boundaryContainer, pressed && styles.pressed]}
        >
            <LinearGradient
                colors={PLUS_GRADIENT}
                start={{ x: 0, y: 0.5 }}
                end={{ x: 1, y: 0.5 }}
                style={styles.boundaryGradient}
            >
                <View style={styles.boundaryLeftPane}>
                    <View style={styles.boundaryStack} pointerEvents="none">
                        <View style={[styles.boundaryStackBar, { opacity: 0.12, marginLeft: 10 }]} />
                        <View style={[styles.boundaryStackBar, { opacity: 0.2, marginLeft: 5 }]} />
                        <View style={[styles.boundaryStackBar, { opacity: 0.28, marginLeft: 0 }]} />
                    </View>
                    <View style={styles.boundaryLeftContent}>
                        <View style={styles.boundaryLockRing}>
                            <Icon name="lock" size={14} color={PLUS_COLORS.iconColor} solid />
                        </View>
                        <Text size="xxl" weight="bold" style={styles.boundaryCount}>
                            {hiddenCount.toLocaleString()}
                        </Text>
                        <Text size="xxs" weight="bold" style={styles.boundaryCountLabel}>
                            {hiddenCount === 1 ? 'message' : 'messages'}
                        </Text>
                    </View>
                </View>

                <View style={styles.boundaryRightPane}>
                    <View style={styles.boundaryEyebrow}>
                        <Icon name="sparkles" size={10} color="rgba(255,255,255,0.9)" solid />
                        <Text size="xxs" weight="bold" style={styles.boundaryEyebrowText}>
                            Plus history
                        </Text>
                    </View>
                    <Text size="l" weight="bold" style={styles.boundaryHeadline}>
                        Your earlier messages are still here
                    </Text>
                    <Text size="s" style={styles.boundarySubtext}>
                        {hiddenCount.toLocaleString()} more above the latest {visibleCap}. Restore Plus to pick up this conversation from the start.
                    </Text>
                    <View style={styles.boundaryCta}>
                        <Icon name="sparkles" size={13} color="#5b2bd6" solid />
                        <Text size="s" weight="bold" style={styles.boundaryCtaText}>Restore Plus</Text>
                        <Icon name="arrow-right" size={11} color="#5b2bd6" solid />
                    </View>
                </View>
            </LinearGradient>
        </Pressable>
    );
}

function SlimTopBar({ hiddenCount, styles, onPress }) {
    const countLabel = hiddenCount.toLocaleString();
    const messageWord = hiddenCount === 1 ? 'message' : 'messages';
    const label = `${countLabel} earlier ${messageWord} · still in your history`;

    return (
        <Pressable
            onPress={onPress}
            accessibilityRole="button"
            accessibilityLabel={`Welcome back to Plus to read ${countLabel} earlier ${messageWord} in your history`}
            style={({ pressed }) => [styles.slimContainer, pressed && styles.pressed]}
        >
            <LinearGradient
                colors={PLUS_GRADIENT}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.slimDot}
            >
                <Icon name="lock" size={12} color={PLUS_COLORS.iconColor} solid />
            </LinearGradient>

            <Text size="s" weight="medium" numberOfLines={1} style={styles.slimLabel}>
                {label}
            </Text>

            <View style={styles.slimCta}>
                <Text size="s" weight="bold" style={styles.slimCtaText}>Welcome back</Text>
                <Icon name="arrow-right" size={11} color={PLUS_COLORS.textColorPurple} solid />
            </View>
        </Pressable>
    );
}

function PlusMessageLockBanner({ hiddenCount, visibleCap, variant = 'boundary' }) {
    const { colors } = useTheme();
    const { isXS } = useResponsiveness();
    const messageWidth = useUIStore(state => state.messageWidth);
    const styles = useMemo(
        () => createStyles(colors, messageWidth, isXS),
        [colors, messageWidth, isXS]
    );
    const { openModal } = useModalStore(state => state.actions);

    const handlePress = useCallback(() => {
        const source = variant === 'top'
            ? 'plus-lock-message-top-bar'
            : 'plus-lock-message-banner';
        openModal('CreditsPurchaseModal', { initialTab: 'subscribe', source });
    }, [openModal, variant]);

    if (!hiddenCount || hiddenCount <= 0) return null;

    return (
        <View style={styles.outerContainer}>
            {variant === 'top' ? (
                <SlimTopBar
                    hiddenCount={hiddenCount}
                    styles={styles}
                    onPress={handlePress}
                />
            ) : (
                <BoundaryBanner
                    hiddenCount={hiddenCount}
                    visibleCap={visibleCap}
                    styles={styles}
                    onPress={handlePress}
                />
            )}
        </View>
    );
}

PlusMessageLockBanner.propTypes = {
    hiddenCount: PropTypes.number.isRequired,
    visibleCap: PropTypes.number,
    variant: PropTypes.oneOf(['boundary', 'top']),
};

PlusMessageLockBanner.defaultProps = {
    visibleCap: 20,
    variant: 'boundary',
};

export default React.memo(PlusMessageLockBanner);
